// Central SEO metadata for every route.
// Page copy lives in seo-pages.json and JSON-LD lives in structured-data.js so
// the build-time prerender script (scripts/prerender-seo.js) and the app always
// stay in sync. Each tag is written once - helpers below update the existing
// element instead of appending, so a page can never end up with duplicate
// title / description / canonical / Open Graph / Twitter / JSON-LD tags.
import config from "./seo-pages.json";
import { SCHEMA_SCRIPT_ID, graphForPath, needsPlans } from "./structured-data";

export const SITE_NAME = config.site.name;
export const SITE_URL = config.site.url;
export const OG_IMAGE = `${config.site.url}${config.site.image}`;
export const OG_IMAGE_ALT = config.site.imageAlt;

export const DEFAULT_SEO = { ...config.pages["/"], path: "/" };

export function seoForPath(pathname) {
  const clean =
    pathname && pathname.length > 1 ? pathname.replace(/\/+$/, "") || "/" : "/";
  if (config.pages[clean]) return { ...config.pages[clean], path: clean };
  for (const entry of config.prefixes) {
    const bare = entry.prefix.replace(/\/$/, "");
    if (clean === bare || clean.startsWith(entry.prefix)) {
      return { ...entry, path: clean };
    }
  }
  return { ...DEFAULT_SEO, path: clean };
}

function setMeta(selector, attr, value, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Write the one and only JSON-LD script for the current page. */
export function setStructuredData(graph) {
  let el = document.getElementById(SCHEMA_SCRIPT_ID);
  if (!graph) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = SCHEMA_SCRIPT_ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(graph);
}

/* ------------------------------------------------------------------ pricing */
// Structured-data offers must mirror the live, purchasable plans, so they are
// read from the billing API (the same source the Pricing page renders) and
// never hard-coded. If the request fails we publish the Service schema without
// offers rather than advertise a price that may be wrong.
let plansPromise = null;

function loadPlans() {
  if (plansPromise) return plansPromise;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  plansPromise = fetch(`${base}/api/billing/plans`)
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => (d && Array.isArray(d.plans) ? d.plans : null))
    .catch(() => null);
  return plansPromise;
}

/** Apply a metadata object (used for both static routes and dynamic pages). */
export function setSeo({
  title,
  description,
  path,
  index = true,
  image,
  ogTitle,
  ogDescription,
  ogType = "website",
  schema,
}) {
  const canonical = `${SITE_URL}${path === "/" ? "/" : path}`;
  const shareImage = image || OG_IMAGE;
  const socialTitle = ogTitle || title;
  const socialDescription = ogDescription || description;

  document.title = title;
  setMeta('meta[name="description"]', "name", "description", description);
  setMeta(
    'meta[name="robots"]',
    "name",
    "robots",
    index ? "index, follow, max-image-preview:large" : "noindex, nofollow"
  );
  setLink("canonical", canonical);

  // Open Graph (Facebook, LinkedIn, iMessage...)
  setMeta('meta[property="og:site_name"]', "property", "og:site_name", SITE_NAME);
  setMeta('meta[property="og:type"]', "property", "og:type", ogType);
  setMeta('meta[property="og:locale"]', "property", "og:locale", "en_US");
  setMeta('meta[property="og:title"]', "property", "og:title", socialTitle);
  setMeta('meta[property="og:description"]', "property", "og:description", socialDescription);
  setMeta('meta[property="og:url"]', "property", "og:url", canonical);
  setMeta('meta[property="og:image"]', "property", "og:image", shareImage);
  setMeta('meta[property="og:image:secure_url"]', "property", "og:image:secure_url", shareImage);
  setMeta('meta[property="og:image:type"]', "property", "og:image:type", "image/png");
  setMeta('meta[property="og:image:width"]', "property", "og:image:width", "1200");
  setMeta('meta[property="og:image:height"]', "property", "og:image:height", "630");
  setMeta('meta[property="og:image:alt"]', "property", "og:image:alt", OG_IMAGE_ALT);

  // Twitter / X - no account is claimed because Sponsor Me has not provided one.
  setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
  setMeta('meta[name="twitter:title"]', "name", "twitter:title", socialTitle);
  setMeta('meta[name="twitter:description"]', "name", "twitter:description", socialDescription);
  setMeta('meta[name="twitter:image"]', "name", "twitter:image", shareImage);
  setMeta('meta[name="twitter:image:alt"]', "name", "twitter:image:alt", OG_IMAGE_ALT);

  // JSON-LD: one <script> per page, replaced on every navigation.
  if (schema !== undefined) {
    setStructuredData(schema);
  }

  return { title, description, path, index };
}

export function applySeo(pathname) {
  const page = seoForPath(pathname);
  const applied = setSeo({ ...page, schema: graphForPath(page, page.path) });

  // Re-emit the graph with live subscription offers once the plans arrive.
  if (needsPlans(page.path)) {
    loadPlans().then((plans) => {
      if (!plans) return;
      const current = seoForPath(window.location.pathname);
      if (current.path !== page.path) return; // user navigated away
      setStructuredData(graphForPath(page, page.path, { plans }));
    });
  }

  return applied;
}
